import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { BlogPostPreview } from '@/api/blogs/get-all-blogs'
import { GetCityRestaurantResponseData } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { UrlConfig } from '@/setup/routing'
import { ArrayItem } from '@/utils/typings/array-item'

export default function BlogsAndRestaurantsSlider({
    blogs,
    restaurants,
    title,
    isLoading,
}: {
    blogs?: BlogPostPreview[]
    restaurants?: GetCityRestaurantResponseData | null
    title: string
    isLoading?: boolean
}) {
    const router = useRouter()
    const t = useTranslations()

    const handleOnClickBlog = (blog: BlogPostPreview) => {
        router.push(
            UrlConfig['/blog/[slugId]'].getUrl({
                slugId: `${blog.slug}-${blog.id}`,
            })
        )
    }

    const handleOnClickRestaurant = (restaurant: ArrayItem<GetCityRestaurantResponseData>) => {
        router.push(
            UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug: restaurant.citySlug,
                restaurantSlug: restaurant.slug,
            })
        )
    }

    const renderSkeleton = () =>
        Array.from({ length: 4 }).map((_, index) => (
            <div
                key={`slider-skeleton-${index}`}
                className="h-80 md:h-96 w-72 md:w-96 p-1 border rounded-xl border-gray-200 flex-shrink-0 snap-start animate-pulse"
            >
                <div className="bg-white overflow-hidden p-2 h-full flex flex-col">
                    <div className="w-full h-36 md:h-48 mb-2 bg-gray-200 rounded-lg" />
                    <div className="flex-1 space-y-2 mt-2">
                        <div className="h-4 w-1/2 bg-gray-200 rounded" />
                        <div className="h-6 w-full bg-gray-200 rounded" />
                        <div className="h-6 w-2/3 bg-gray-200 rounded mt-auto mb-2" />
                    </div>
                </div>
            </div>
        ))

    return (
        <div className="w-full flex flex-col items-left md:items-center justify-center mt-0 md:mt-4 pl-4 md:pl-0">
            <h2 className="text-[24px] md:text-[32px] w-full text-left md:text-center max-w-lg">
                {title}
            </h2>
            <div className="mt-4 w-full overflow-x-auto scrollbar-hide">
                <div className="flex flex-nowrap gap-4 pl-3 md:pl-10 pb-2 snap-x snap-mandatory scroll-smooth">
                    {isLoading ? (
                        renderSkeleton()
                    ) : (
                        <>
                            {blogs?.map((blog, index) => (
                                <div
                                    key={blog.id}
                                    className="h-80 md:h-96 w-72 md:w-96 p-1 border rounded-xl border-gray-200 flex-shrink-0 snap-start"
                                    onClick={() => handleOnClickBlog(blog)}
                                >
                                    <div className="bg-white overflow-hidden p-2 cursor-pointer h-full flex flex-col">
                                        <div className="relative w-full h-36 md:h-48 mb-2">
                                            <Image
                                                src={`https:${blog.coverImage}`}
                                                alt={blog.title}
                                                fill
                                                className="object-cover rounded-lg"
                                                sizes="(max-width: 768px) 100vw, 33vw"
                                            />
                                        </div>
                                        <div className="flex flex-col flex-1">
                                            <p className="text-black/60 text-sm">
                                                {blog.topics?.map((topic) => topic.name).join(', ')}
                                            </p>
                                            <h3 className="text-lg font-semibold">
                                                {blog.title.length > 80
                                                    ? blog.title.slice(
                                                          0,
                                                          blog.title.slice(0, 70).lastIndexOf(' ')
                                                      ) + '...'
                                                    : blog.title}
                                            </h3>
                                            <div className="mt-auto mb-2">
                                                <Button
                                                    key={index}
                                                    styleType="tertiary"
                                                    shape="pill"
                                                    className="text-xs md:text-sm"
                                                    href={{
                                                        pattern: '/blog/[slugId]',
                                                        params: {
                                                            slugId: `${blog.slug}-${blog.id}`,
                                                        },
                                                    }}
                                                >
                                                    {t('blog.slider.readMore')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {restaurants?.map((restaurant) => (
                                <div
                                    key={restaurant.uuid}
                                    className="h-80 md:h-96 w-72 md:w-96 p-1 border rounded-xl border-gray-200 flex-shrink-0 snap-start"
                                    onClick={() => handleOnClickRestaurant(restaurant)}
                                >
                                    <div className="bg-white overflow-hidden p-2 cursor-pointer h-full flex flex-col">
                                        <div className="relative w-full h-36 md:h-48 mb-2">
                                            {restaurant.images && restaurant.images.length >= 1 && (
                                                <Image
                                                    src={`${restaurant.images[0]?.url}`}
                                                    alt={restaurant.name}
                                                    fill
                                                    className="object-cover rounded-lg"
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                />
                                            )}
                                        </div>
                                        <div className="flex flex-col flex-1">
                                            <p className="text-sm text-black/60 ">
                                                {restaurant.tags?.map((tag) => tag.name).join(', ')}
                                            </p>
                                            <h3 className="text-lg font-semibold">
                                                {restaurant.name.length > 40
                                                    ? restaurant.name.slice(
                                                          0,
                                                          restaurant.name
                                                              .slice(0, 30)
                                                              .lastIndexOf(' ')
                                                      ) + '...'
                                                    : restaurant.name}
                                            </h3>
                                            <div className="flex flex-row flex-nowrap gap-2 mt-auto mb-2">
                                                {restaurant.deals?.slice(0, 2)?.map(
                                                    (deal, index) =>
                                                        deal.name && (
                                                            <Button
                                                                key={index}
                                                                styleType="primary"
                                                                shape="pill"
                                                                className="text-xs md:text-sm"
                                                                href={{
                                                                    pattern:
                                                                        '/restaurants/[citySlug]/[restaurantSlug]',
                                                                    params: {
                                                                        citySlug:
                                                                            restaurant.citySlug,
                                                                        restaurantSlug:
                                                                            restaurant.slug,
                                                                    },
                                                                }}
                                                            >
                                                                {deal.name.length > 30
                                                                    ? deal.name.slice(
                                                                          0,
                                                                          deal.name
                                                                              .slice(0, 20)
                                                                              .lastIndexOf(' ')
                                                                      ) + '...'
                                                                    : deal.name}
                                                            </Button>
                                                        )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    <div className="flex-shrink-0 w-[5%]" />
                </div>
            </div>
        </div>
    )
}
